<template>
  <div class="email-settings">
    <!-- Header -->
    <div class="es-header mb-4">
      <h4 class="fw-semi-bold mb-2">
        <i class="fa fa-envelope mr-2"></i>
        Email Settings
      </h4>
      <p class="text-muted mb-0">
        Set up a verified email sender so your practice can send campaigns, reminders, and confirmations.
      </p>
    </div>

    <!-- Email Display Name Setting (shown in every state; disabled once email is active) -->
    <b-card class="mb-4">
      <div class="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h6 class="fw-semi-bold mb-1">
            <i class="fa fa-id-card-o mr-1"></i> Email Display Name
          </h6>
          <small class="text-muted">This name appears as the sender in your patients' inbox (e.g., "{{ practiceDisplayName || 'Mint Dental' }}")</small>
        </div>
        <b-button
          variant="success"
          size="sm"
          @click="saveEmailFromName"
          :disabled="savingFromName || isReadOnly || authUnavailable"
        >
          <b-spinner v-if="savingFromName" small class="mr-1" />
          Save
        </b-button>
      </div>
      <b-form-input
        v-model="emailFromName"
        :placeholder="practiceDisplayName ? 'e.g., ' + practiceDisplayName : 'e.g., Mint Dental'"
        class="mt-2"
        style="max-width: 400px;"
        :disabled="isReadOnly"
      />
    </b-card>

    <!-- ============================================================ -->
    <!-- Email Signature (2026-09-01)                                 -->
    <!--                                                              -->
    <!-- Stored as ONE settings row, EMAIL_SIGNATURE, holding the      -->
    <!-- config AND the html/text this screen renders. Backend senders -->
    <!-- read that html VERBATIM (see src/utils/emailSignature.js).    -->
    <!--                                                              -->
    <!-- ⛔ NOT gated on isReadOnly, deliberately. The no-touch rule    -->
    <!-- exists for SENDER IDENTITY (shared SendGrid resources,        -->
    <!-- CAN-SPAM verification); a signature is content, and the only  -->
    <!-- practices it is useful to are exactly the ACTIVE ones. Gating -->
    <!-- it would hand the feature to nobody but Super Admin — the     -->
    <!-- same closed loop that hid the TxP office-email field from the -->
    <!-- practices that needed it.                                     -->
    <!-- ============================================================ -->
    <b-card class="signature-card mb-4">
      <div class="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h6 class="fw-semi-bold mb-1">
            <i class="fa fa-pencil-square-o mr-1"></i> Email Signature
          </h6>
          <small class="text-muted">
            Your practice's signature — logo, name, phone, address and links. Teamio adds it
            automatically to the bottom of <strong>Treatment Plan (Tx Presenter)</strong> patient
            emails. For Wiki email templates and campaigns, place
            <code v-pre>{{email_signature_block}}</code> where you want it (Wiki editor's
            <strong>+ Insert</strong> menu) — templates without it are unchanged.
          </small>
        </div>
        <b-button
          variant="success"
          size="sm"
          @click="saveSignature"
          :disabled="savingSignature || uploadingSignatureLogo"
        >
          <b-spinner v-if="savingSignature" small class="mr-1" />
          Save
        </b-button>
      </div>

      <b-form-checkbox v-model="signature.enabled" switch class="sig-toggle mt-3 mb-3">
        <span class="sig-toggle-text">
          <strong>Use my signature in patient emails</strong>
          <span class="sig-toggle-sub">
            Off: nothing changes and <code v-pre>{{email_signature_block}}</code> renders blank.
            On: added to Treatment Plan emails automatically, and filled in wherever you have
            placed the variable.
          </span>
        </span>
      </b-form-checkbox>

      <!-- Logo -->
      <div class="sig-logo-row mb-3">
        <div class="sig-logo-frame">
          <img
            v-if="signatureLogoUrl && !signatureLogoBroken"
            class="sig-logo-preview"
            :src="signatureLogoUrl"
            alt="Signature logo preview"
            @load="onSignatureLogoLoad($event)"
            @error="onSignatureLogoLoad(null)"
          >
          <span v-else class="sig-logo-empty">
            {{ signatureLogoBroken ? "Can't load" : 'No logo' }}
          </span>
        </div>
        <div class="sig-logo-fields">
          <!-- UPLOAD is the primary path. A practice's website logo is very often a .webp
               or a CSS background — it looks fine on the site and renders as nothing in a
               patient's mail app. Uploading converts to PNG, which every client shows. -->
          <div class="sig-logo-actions mb-1">
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="uploadingSignatureLogo"
              @click="$refs.sigLogoFile.click()"
            >
              <template v-if="uploadingSignatureLogo">
                <b-spinner small class="mr-1" /> Uploading&hellip;
              </template>
              <template v-else>
                <i class="fa fa-upload mr-1"></i> Upload logo
              </template>
            </b-button>
            <input
              ref="sigLogoFile"
              type="file"
              :accept="acceptedLogoExtensions"
              class="d-none"
              @change="onSignatureLogoFilePicked"
            >
            <b-link
              v-if="signature.fields.logoUrl"
              class="small"
              @click="removeSignatureLogo"
            >
              Remove logo
            </b-link>
          </div>
          <small class="text-muted d-block mb-2">
            PNG, JPG, GIF, WebP or SVG, up to 10MB. Converted to PNG so every email app can
            show it; animation isn't kept.
          </small>

          <b-link
            v-if="!signatureLogoPasteVisible"
            class="small"
            @click="signatureLogoPasteOpen = true"
          >
            or paste a link to your logo
          </b-link>
          <b-form-group
            v-if="signatureLogoPasteVisible"
            label="Logo link"
            label-class="form-label-custom"
            class="mb-1"
          >
            <b-form-input
              v-model="signature.fields.logoUrl"
              type="text"
              trim
              placeholder="https://your-practice.com/logo.png"
            />
            <small v-if="signatureLogoPasteWarning" class="text-warning d-block mt-1">
              Some email apps may not display .webp or .svg images. Upload the file instead
              and Teamio will convert it.
            </small>
          </b-form-group>

          <b-form-checkbox v-model="signature.fields.showLogo" class="sig-show-logo">
            Show the logo in my signature
          </b-form-checkbox>
          <small v-if="signatureLogoBroken" class="text-danger d-block mt-1">
            That link didn't load as an image. Check the URL, or clear it to leave the logo out.
          </small>
          <small v-else class="text-muted d-block mt-1">
            Scaled to fit {{ signatureLogoBoxLabel }} in the email.
          </small>
        </div>
      </div>

      <!-- Practice details -->
      <b-row>
        <b-col md="6">
          <b-form-group label="Practice Name" label-class="form-label-custom">
            <b-form-input v-model="signature.fields.practiceName" placeholder="e.g., Mint Dental" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Phone" label-class="form-label-custom">
            <b-form-input v-model="signature.fields.phone" placeholder="e.g., (561) 318-0805" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-form-group label="Address Line 1" label-class="form-label-custom">
            <b-form-input v-model="signature.fields.address1" placeholder="e.g., 13475 Southern Blvd" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Address Line 2" label-class="form-label-custom">
            <b-form-input v-model="signature.fields.address2" placeholder="e.g., Unit 201" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="5">
          <b-form-group label="City" label-class="form-label-custom">
            <b-form-input v-model="signature.fields.city" placeholder="e.g., Loxahatchee Groves" />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="State" label-class="form-label-custom">
            <b-form-input v-model="signature.fields.state" placeholder="e.g., FL" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="ZIP" label-class="form-label-custom">
            <b-form-input v-model="signature.fields.zip" placeholder="e.g., 33470" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-form-group label="Website" label-class="form-label-custom">
            <b-form-input v-model="signature.fields.website" placeholder="e.g., www.yourpractice.com" />
            <small v-if="signatureWebsiteInvalid" class="text-danger d-block">
              That doesn't look like a web address — it won't appear in the signature.
            </small>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Links heading" label-class="form-label-custom">
            <b-form-input v-model="signature.fields.tagline" placeholder="e.g., Follow us:" />
            <small class="text-muted">Shown just above your links.</small>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Links -->
      <div class="sig-links">
        <label class="form-label-custom d-block mb-2">Links</label>
        <div v-for="(link, i) in signature.links" :key="'sig-link-' + i" class="sig-link-row">
          <b-form-input v-model="link.label" class="sig-link-label" placeholder="Label" />
          <b-form-input v-model="link.value" class="sig-link-value" placeholder="@handle or https://..." />
          <b-button
            variant="link"
            class="sig-link-remove p-0"
            v-b-tooltip.hover
            title="Remove"
            @click="removeSignatureLink(i)"
          >
            <i class="fa fa-times"></i>
          </b-button>
        </div>
        <p v-if="!signature.links.length" class="sig-links-empty">
          No links yet. A handle like <code>@yourpractice</code> shows as text; a full
          <code>https://</code> address becomes a clickable link.
        </p>
        <div class="sig-link-actions">
          <b-button size="sm" variant="outline-secondary" @click="addSignatureLink('Instagram')">
            <i class="fa fa-instagram mr-1"></i> Instagram
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="addSignatureLink('Facebook')">
            <i class="fa fa-facebook mr-1"></i> Facebook
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="addSignatureLink('')">
            <i class="fa fa-plus mr-1"></i> Custom link
          </b-button>
        </div>
      </div>

      <!-- Live preview. v-html is safe here: this markup is OUR renderer's output, not
           anything a user typed — every field it carries has been HTML-escaped by
           src/utils/emailSignature.js and only http(s) URLs ever become an href/src. -->
      <div class="sig-preview mt-3" :class="{ 'sig-preview--off': !signature.enabled }">
        <div class="sig-preview-head">
          Preview
          <span v-if="!signature.enabled" class="sig-preview-off-pill">Turned off</span>
        </div>
        <div v-if="renderedSignature.html" class="sig-preview-body" v-html="renderedSignature.html"></div>
        <div v-else class="sig-preview-empty">
          Nothing to show yet — add your practice name, a logo, or a link.
        </div>
      </div>

      <p v-if="!signature.enabled" class="sig-note">
        This is a preview only. The signature is turned off, so it isn't added to any email.
      </p>
      <p v-if="signatureBlocked" class="sig-error">
        <i class="fa fa-exclamation-triangle mr-1"></i> {{ SIGNATURE_EMPTY_MESSAGE }}
      </p>
    </b-card>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">Loading email settings...</p>
    </div>

    <!-- Main Content -->
    <div v-else class="email-settings-content">

      <!-- Signed out of Firebase (no token even after session restore) — every
           email-settings call would 401, so warn instead of rendering a broken tab. -->
      <b-alert v-if="authUnavailable" show variant="warning" class="mb-0 auth-warn">
        <i class="fa fa-exclamation-triangle mr-2"></i>
        Your session needs a fresh login to manage email settings. Please refresh the page
        or sign in again, then reopen this tab.
      </b-alert>

      <template v-else>
      <!-- ============================================================ -->
      <!-- STATE: ACTIVE — email already works. Read-only for staff.    -->
      <!-- ============================================================ -->
      <template v-if="emailState === 'active'">
        <b-card class="active-summary-card mb-4">
          <template #header>
            <div class="card-header-content">
              <div class="header-left">
                <i class="fa fa-check-circle text-success"></i>
                <span class="header-title">Email is active</span>
              </div>
              <b-badge variant="success">Active</b-badge>
            </div>
          </template>

          <div class="sender-details">
            <div class="detail-row">
              <span class="detail-label">From Name:</span>
              <span class="detail-value">{{ activeFromName || '—' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Sender Email:</span>
              <span class="detail-value">{{ activeFromEmail || '—' }}</span>
            </div>
            <div v-if="authenticatedDomain" class="detail-row">
              <span class="detail-label">Domain Auth:</span>
              <b-badge :variant="authenticatedDomain.valid ? 'success' : 'warning'">
                {{ authenticatedDomain.valid ? 'Authenticated' : 'DNS Pending' }}
              </b-badge>
            </div>
          </div>

          <b-alert show variant="light" class="support-note mt-3 mb-0">
            <i class="fa fa-lock mr-2 text-muted"></i>
            Email is active for your practice. To change your sender, contact Teamio support.
          </b-alert>
        </b-card>

        <!-- What you can do now -->
        <b-card class="done-card mb-4">
          <h6 class="fw-semi-bold mb-3">
            <i class="fa fa-paper-plane text-success mr-2"></i> You're all set to send email
          </h6>
          <ul class="done-list mb-0">
            <li><i class="fa fa-bullhorn text-muted mr-2"></i> Send <strong>Campaigns</strong> to your patient lists</li>
            <li><i class="fa fa-bell-o text-muted mr-2"></i> Automate appointment <strong>Reminders</strong> and recall</li>
            <li><i class="fa fa-check-square-o text-muted mr-2"></i> Send booking <strong>Confirmations</strong> by email</li>
          </ul>
        </b-card>

        <!-- Super Admin ONLY: management controls (global SendGrid deletes) -->
        <template v-if="canSeeRemove">
          <b-card class="sa-management-card mb-4">
            <template #header>
              <div class="card-header-content">
                <div class="header-left">
                  <i class="fa fa-shield"></i>
                  <span class="header-title">Sender management</span>
                </div>
                <b-badge variant="warning">Super Admin</b-badge>
              </div>
            </template>

            <p class="text-muted small">
              These actions delete senders/domains from the shared SendGrid account. Staff cannot see them.
            </p>

            <div v-if="verifiedSender" class="mb-3">
              <div class="detail-row">
                <span class="detail-label">Verified Sender:</span>
                <span class="detail-value">{{ verifiedSender.from_email }}</span>
              </div>
              <b-button variant="outline-danger" size="sm" class="mt-2" @click="confirmDelete">
                <i class="fa fa-trash mr-1"></i> Remove Sender
              </b-button>
            </div>

            <div v-if="authenticatedDomain">
              <div class="detail-row">
                <span class="detail-label">Domain:</span>
                <span class="detail-value">{{ authenticatedDomain.domain }}</span>
              </div>
              <b-button variant="outline-danger" size="sm" class="mt-2" @click="confirmDeleteDomain">
                <i class="fa fa-trash mr-1"></i> Remove Domain
              </b-button>
            </div>
          </b-card>
        </template>
      </template>

      <!-- ============================================================ -->
      <!-- STATE: PENDING — resume / wait screen with poll.             -->
      <!-- ============================================================ -->
      <template v-else-if="emailState === 'pending'">

        <!-- Single-sender: confirmation-email wait screen -->
        <b-card v-if="pendingSender" class="pending-sender-card mb-4">
          <template #header>
            <div class="card-header-content">
              <div class="header-left">
                <i class="fa fa-clock-o text-warning"></i>
                <span class="header-title">Email Verification Pending</span>
              </div>
              <b-badge variant="warning">Action Required</b-badge>
            </div>
          </template>

          <div class="pending-instructions">
            <div class="instruction-header mb-3">
              <i class="fa fa-envelope-open text-warning mr-2"></i>
              <strong>Check your email at {{ pendingSender.from_email }}</strong>
            </div>

            <ol class="instruction-steps">
              <li>Look for an email from <strong>SendGrid</strong> with subject "Verify your sender identity"</li>
              <li>Click the <strong>verification link</strong> in that email</li>
              <li>We check automatically — or click <strong>"Check Status"</strong> to refresh now</li>
            </ol>

            <b-alert show variant="light" class="tip-alert mb-3">
              <i class="fa fa-lightbulb-o mr-2 text-warning"></i>
              <strong>Tip:</strong> Check your spam/junk folder if you don't see the email within a few minutes.
              We re-check for you about once a minute.
            </b-alert>
          </div>

          <div class="pending-actions">
            <b-button variant="primary" size="sm" @click="checkVerificationStatus" :disabled="checking">
              <b-spinner v-if="checking" small class="mr-1" />
              <i v-else class="fa fa-check-circle mr-1"></i>
              Check Status
            </b-button>
            <b-button variant="outline-warning" size="sm" class="ml-2" @click="resendVerificationForPending" :disabled="resending">
              <b-spinner v-if="resending" small class="mr-1" />
              <i v-else class="fa fa-refresh mr-1"></i>
              Resend Email
            </b-button>
            <!-- Start Over is a PENDING-only control (never in ACTIVE) -->
            <b-button variant="outline-secondary" size="sm" class="ml-2" @click="cancelPending">
              <i class="fa fa-times mr-1"></i>
              Start Over
            </b-button>
          </div>
        </b-card>

        <!-- Domain authentication: DNS records to paste + validate -->
        <b-card v-if="authenticatedDomain" class="pending-domain-card mb-4">
          <template #header>
            <div class="card-header-content">
              <div class="header-left">
                <i class="fa fa-globe text-warning"></i>
                <span class="header-title">Add your DNS records</span>
              </div>
              <b-badge :variant="authenticatedDomain.valid ? 'success' : 'warning'">
                {{ authenticatedDomain.valid ? 'Verified' : 'DNS Pending' }}
              </b-badge>
            </div>
          </template>

          <div class="detail-row mb-3">
            <span class="detail-label">Domain:</span>
            <span class="detail-value">{{ authenticatedDomain.domain }}</span>
          </div>

          <p class="text-muted">
            Add these records to your domain's DNS settings (through your registrar — GoDaddy, Namecheap, etc.).
            Changes can take up to 48 hours to propagate. Copy each host and value exactly.
          </p>

          <div class="dns-records">
            <div v-for="rec in dnsRecords" :key="rec.key" class="dns-record">
              <div class="dns-record-head">
                <span class="dns-type">{{ rec.type }}</span>
                <b-badge :variant="rec.valid ? 'success' : 'warning'">{{ rec.valid ? 'Verified' : 'Pending' }}</b-badge>
              </div>
              <div class="dns-field">
                <label>Host / Name</label>
                <div class="dns-copy">
                  <code>{{ rec.host }}</code>
                  <b-button variant="link" size="sm" class="copy-btn p-0" @click="copyToClipboard(rec.host)" v-b-tooltip.hover title="Copy">
                    <i class="fa fa-copy"></i>
                  </b-button>
                </div>
              </div>
              <div class="dns-field">
                <label>Value / Points To</label>
                <div class="dns-copy">
                  <code>{{ rec.data }}</code>
                  <b-button variant="link" size="sm" class="copy-btn p-0" @click="copyToClipboard(rec.data)" v-b-tooltip.hover title="Copy">
                    <i class="fa fa-copy"></i>
                  </b-button>
                </div>
              </div>
              <!-- SendGrid's per-record reason, shown verbatim -->
              <div v-if="!rec.valid && rec.reason" class="dns-reason">
                <i class="fa fa-exclamation-circle mr-1"></i>{{ rec.reason }}
              </div>
            </div>
          </div>

          <div class="domain-email-field mt-3">
            <label class="form-label-custom d-block mb-1">Sending Email Address</label>
            <b-form-input
              v-model="domainSendingEmail"
              type="email"
              placeholder="e.g., office@yourbusiness.com"
              style="max-width: 400px;"
              :state="domainSendingEmail ? domainEmailValid : null"
            />
            <small v-if="domainEmailMismatch" class="text-danger d-block">
              This address must be at <strong>{{ authenticatedDomain.domain }}</strong>.
            </small>
            <small v-else class="text-muted d-block">
              We enable this address once your DNS verifies — it must be at {{ authenticatedDomain.domain }}.
            </small>
          </div>

          <div class="pending-actions mt-3">
            <b-button variant="primary" size="sm" @click="checkDomainAgain" :disabled="validating">
              <b-spinner v-if="validating" small class="mr-1" />
              <i v-else class="fa fa-check-circle mr-1"></i>
              Check again
            </b-button>
            <!-- Recipients are chosen by the server (the practice's Admins); the records are
                 re-read from SendGrid server-side, never sent from here. -->
            <b-button
              variant="outline-primary"
              size="sm"
              data-test="email-dns-records-btn"
              :disabled="sendingDnsEmail"
              @click="emailDnsRecordsToAdmins"
            >
              <b-spinner v-if="sendingDnsEmail" small class="mr-1" />
              <i v-else class="fa fa-envelope mr-1"></i>
              Email these records to our admins
            </b-button>
            <!-- Global SendGrid delete — Super Admin only -->
            <b-button v-if="canSeeRemove" variant="outline-danger" size="sm" class="ml-2" @click="confirmDeleteDomain">
              <i class="fa fa-trash mr-1"></i>
              Remove Domain
            </b-button>
          </div>

          <!-- Teamio staff only: also send to extra addresses (e.g. the practice's IT vendor). -->
          <div v-if="isSuperAdmin" class="dns-extra-recipients mt-2">
            <label class="form-label-custom d-block mb-1" for="dns-extra-recipients">
              Also send to (optional, Teamio staff only, up to 5, comma-separated)
            </label>
            <input
              id="dns-extra-recipients"
              v-model="dnsExtraRecipients"
              type="text"
              class="dns-extra-input"
              data-test="email-dns-extra-recipients"
              placeholder="it@vendor.com, office@clinic.com"
              :disabled="sendingDnsEmail"
            />
          </div>
        </b-card>
      </template>

      <!-- ============================================================ -->
      <!-- STATE: NONE — guided wizard.                                 -->
      <!-- ============================================================ -->
      <template v-else>

        <!-- Step 1: Verification Method Choice -->
        <b-card class="method-choice-card mb-4">
          <template #header>
            <div class="card-header-content">
              <div class="header-left">
                <i class="fa fa-cog"></i>
                <span class="header-title">Choose how to set up email</span>
              </div>
            </div>
          </template>

          <p class="text-muted mb-4">
            <strong>Domain Authentication</strong> connects your practice's own website domain, so email arrives from
            your address with the best possible deliverability and without "via sendgrid.net". It needs three DNS
            records — your web host or IT can add them in a few minutes.
          </p>

          <div class="method-options">
            <!-- Domain Authentication (recommended) -->
            <div class="method-option" :class="{ 'selected': selectedMethod === 'domain' }" @click="selectedMethod = 'domain'">
              <div class="method-icon"><i class="fa fa-shield"></i></div>
              <div class="method-content">
                <h6 class="method-title">
                  Domain Authentication
                  <b-badge variant="success" class="ml-2">Recommended</b-badge>
                </h6>
                <p class="method-description mb-2">
                  Authenticate your practice's website domain with DNS records. Best deliverability and a professional
                  appearance.
                </p>
                <ul class="method-features">
                  <li><i class="fa fa-check text-success mr-1"></i> Removes "via sendgrid.net" from emails</li>
                  <li><i class="fa fa-check text-success mr-1"></i> Better inbox placement</li>
                  <li><i class="fa fa-exclamation-circle text-warning mr-1"></i> Requires DNS access (or help from IT)</li>
                </ul>
              </div>
              <div class="method-select">
                <i class="fa" :class="selectedMethod === 'domain' ? 'fa-check-circle text-primary' : 'fa-circle-o text-muted'"></i>
              </div>
            </div>

            <!-- ⛔ SINGLE SENDER WAS REMOVED HERE — 2026-08-29 (decision D2).
                 DO NOT PUT IT BACK without re-reading
                 teamio-serverless-api/tx-presenter-service/docs/2026-08-14-platform-email-sender-plan.md → D2.

                 It offered zero-DNS setup, was badged "Quick & Easy", and sat directly under Domain
                 Authentication's "Requires DNS access" caveat — so offices chose it. But with a Single
                 Sender NEITHER SPF NOR DKIM ALIGNS with the practice's own domain, so against any
                 recipient publishing p=quarantine the mail is SILENTLY JUNKED while SendGrid still
                 reports "delivered". That is invisible non-delivery of clinical documents behind a
                 green dashboard — the worst failure mode available here.

                 Removing it stranded nobody: verified live against the prod SendGrid account
                 2026-08-29, the only verified single senders are three internal Teamio addresses, and
                 the one client attempt (contact@mintdentalhealth.com) was never verified.

                 The zero-DNS need it existed to serve is now met properly by the platform sender
                 (<slug>@mail.teamio.app, practice display name, office Reply-To), enabled per-site
                 from SuperAdmin. That path is authenticated and DMARC-aligned; this one never was.

                 Only the PICKER entry is gone. The backend /verified_senders endpoints and the
                 `selectedMethod === 'single'` branches below remain but are now unreachable —
                 deliberately left in place so this is a one-line revert, not an excavation. -->
          </div>
        </b-card>

        <!-- Step 2a: Single Sender Form (CAN-SPAM) -->
        <b-card v-if="selectedMethod === 'single'" class="sender-form-card">
          <template #header>
            <div class="card-header-content">
              <div class="header-left">
                <i class="fa fa-envelope"></i>
                <span class="header-title">Single Sender</span>
              </div>
            </div>
          </template>

          <div class="how-it-works mb-4">
            <h6 class="mb-3"><i class="fa fa-info-circle text-info mr-2"></i>How It Works</h6>
            <div class="steps-inline">
              <div class="step-inline"><span class="step-num">1</span><span>Fill out the form below</span></div>
              <i class="fa fa-arrow-right text-muted mx-2"></i>
              <div class="step-inline"><span class="step-num">2</span><span>Check your inbox for the verification email</span></div>
              <i class="fa fa-arrow-right text-muted mx-2"></i>
              <div class="step-inline"><span class="step-num">3</span><span>Click the link to verify</span></div>
            </div>
          </div>

          <b-alert show variant="warning" class="mb-4">
            <i class="fa fa-exclamation-triangle mr-2"></i>
            <strong>Important:</strong> Use a professional email from your business domain (e.g., contact@yourdomain.com).
            Free email providers (Gmail, Yahoo, AOL) may have delivery issues.
          </b-alert>

          <b-form @submit.prevent="submitVerification">
            <div class="form-section">
              <h6 class="form-section-title"><i class="fa fa-user mr-2"></i>Sender Information</h6>
              <b-row>
                <b-col md="6">
                  <b-form-group label="From Name *" label-class="form-label-custom">
                    <b-form-input v-model="senderForm.from_name" placeholder="e.g., Mint Dental" :state="getFieldState('from_name')" required />
                    <b-form-invalid-feedback>From name is required</b-form-invalid-feedback>
                    <small class="text-muted">Displayed as sender name in recipient's inbox</small>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="From Email *" label-class="form-label-custom">
                    <b-form-input v-model="senderForm.from_email" type="email" placeholder="e.g., contact@yourdomain.com" :state="getFieldState('from_email')" required />
                    <b-form-invalid-feedback>Valid business email is required</b-form-invalid-feedback>
                    <small class="text-muted">Verification email will be sent here</small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6">
                  <b-form-group label="Reply-To Email" label-class="form-label-custom">
                    <b-form-input v-model="senderForm.reply_to" type="email" placeholder="Same as From Email if blank" />
                    <small class="text-muted">Where replies will go (optional)</small>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Nickname" label-class="form-label-custom">
                    <b-form-input v-model="senderForm.nickname" placeholder="e.g., Main Office Email" />
                    <small class="text-muted">Internal label for your reference</small>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <div class="form-section">
              <h6 class="form-section-title">
                <i class="fa fa-building mr-2"></i>Business Address
                <small class="text-muted ml-2">(Required by CAN-SPAM regulations)</small>
              </h6>
              <b-row>
                <b-col md="12">
                  <b-form-group label="Street Address *" label-class="form-label-custom">
                    <b-form-input v-model="senderForm.address" placeholder="e.g., 123 Main Street, Suite 100" :state="getFieldState('address')" required />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="4">
                  <b-form-group label="City *" label-class="form-label-custom">
                    <b-form-input v-model="senderForm.city" placeholder="e.g., Emerson" :state="getFieldState('city')" required />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="State" label-class="form-label-custom">
                    <b-form-input v-model="senderForm.state" placeholder="e.g., NJ" />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="ZIP Code" label-class="form-label-custom">
                    <b-form-input v-model="senderForm.zip" placeholder="e.g., 07630" />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="4">
                  <b-form-group label="Country *" label-class="form-label-custom">
                    <b-form-select v-model="senderForm.country" :options="countryOptions" :state="getFieldState('country')" required />
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <div class="form-actions">
              <b-button variant="outline-secondary" class="mr-2" @click="selectedMethod = null">
                <i class="fa fa-arrow-left mr-1"></i> Back
              </b-button>
              <b-button variant="success" type="submit" :disabled="submitting || !isFormValid">
                <b-spinner v-if="submitting" small class="mr-1" />
                <i v-else class="fa fa-paper-plane mr-1"></i>
                Send Verification Email
              </b-button>
            </div>
          </b-form>
        </b-card>

        <!-- Step 2b: Domain Authentication Form -->
        <b-card v-if="selectedMethod === 'domain'" class="domain-form-card">
          <template #header>
            <div class="card-header-content">
              <div class="header-left">
                <i class="fa fa-shield"></i>
                <span class="header-title">Domain Authentication</span>
              </div>
            </div>
          </template>

          <div class="how-it-works mb-4">
            <h6 class="mb-3"><i class="fa fa-info-circle text-info mr-2"></i>How It Works</h6>
            <div class="steps-inline">
              <div class="step-inline"><span class="step-num">1</span><span>Enter your domain</span></div>
              <i class="fa fa-arrow-right text-muted mx-2"></i>
              <div class="step-inline"><span class="step-num">2</span><span>Add the CNAME records to your registrar</span></div>
              <i class="fa fa-arrow-right text-muted mx-2"></i>
              <div class="step-inline"><span class="step-num">3</span><span>Verify DNS propagation</span></div>
            </div>
          </div>

          <b-alert show variant="info" class="mb-4">
            <i class="fa fa-info-circle mr-2"></i>
            <strong>Note:</strong> You'll need access to your domain's DNS settings (usually through your domain registrar
            like GoDaddy, Namecheap, Google Domains, etc.) or help from your IT team.
          </b-alert>

          <b-form @submit.prevent="submitDomainAuth">
            <div class="form-section">
              <h6 class="form-section-title"><i class="fa fa-globe mr-2"></i>Domain Information</h6>
              <b-row>
                <b-col md="6">
                  <b-form-group label="Domain *" label-class="form-label-custom">
                    <b-form-input v-model="domainForm.domain" placeholder="e.g., yourbusiness.com" :state="getDomainFieldState('domain')" required />
                    <b-form-invalid-feedback>Valid domain is required</b-form-invalid-feedback>
                    <small class="text-muted">Your business domain (without www or http)</small>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Subdomain (optional)" label-class="form-label-custom">
                    <b-form-input v-model="domainForm.subdomain" placeholder="e.g., mail or em" />
                    <small class="text-muted">Leave blank for default (em####)</small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6">
                  <b-form-group label="Sending Email Address *" label-class="form-label-custom">
                    <b-form-input
                      v-model="domainSendingEmail"
                      type="email"
                      placeholder="e.g., office@yourdomain.com"
                      :state="domainSendingEmail ? domainEmailValid : null"
                    />
                    <small v-if="domainEmailMismatch" class="text-danger d-block">
                      This address must be at <strong>{{ domainForm.domain || 'your domain' }}</strong>
                      (e.g., office@{{ domainForm.domain || 'yourdomain.com' }}).
                    </small>
                    <small v-else class="text-muted">The address you'll send from — must be at the domain above.</small>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <div class="form-actions">
              <b-button variant="outline-secondary" class="mr-2" @click="selectedMethod = null">
                <i class="fa fa-arrow-left mr-1"></i> Back
              </b-button>
              <b-button variant="success" type="submit" :disabled="submitting || !isDomainFormValid">
                <b-spinner v-if="submitting" small class="mr-1" />
                <i v-else class="fa fa-cogs mr-1"></i>
                Generate DNS Records
              </b-button>
            </div>
          </b-form>
        </b-card>
      </template>
      </template>
    </div>

    <!-- Delete Confirmation Modal (Super Admin only) -->
    <b-modal v-model="showDeleteModal" title="Remove Sender" centered @ok="deleteSender">
      <p>Are you sure you want to remove this verified sender?</p>
      <p class="text-muted"><strong>{{ verifiedSender && verifiedSender.from_email }}</strong></p>
      <p class="text-danger">
        <i class="fa fa-exclamation-triangle mr-1"></i>
        This deletes the sender from the shared SendGrid account. You will need to re-verify to use it again.
      </p>
    </b-modal>

    <!-- Delete Domain Confirmation Modal (Super Admin only) -->
    <b-modal v-model="showDeleteDomainModal" title="Remove Domain Authentication" centered @ok="deleteDomain">
      <p>Are you sure you want to remove domain authentication for:</p>
      <p class="text-muted"><strong>{{ authenticatedDomain && authenticatedDomain.domain }}</strong></p>
      <p class="text-danger">
        <i class="fa fa-exclamation-triangle mr-1"></i>
        This deletes the domain from the shared SendGrid account. You will need to re-authenticate to use it again.
      </p>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import { isEmailEnabled, readSettingValue, EMAIL_CAPABILITY_SETTING } from '@/utils/siteCapability.js';
import { pickVerifiedFromEmail } from '@/utils/verifiedSender.js';
import {
  renderEmailSignature,
  isRenderableSignatureConfig,
  asciiSafeStringifySignature,
  parseSignatureSetting,
  normalizeWebsiteUrl,
  sanitizeSignatureUrl,
  emptySignatureFields,
  SIGNATURE_VERSION,
  LOGO_BOX,
} from '@/utils/emailSignature.js';
// The SHARED uploader — same presigned-POST path Forms/Wiki/Chat use. Never a second
// implementation: one place to fix a bucket, a policy or a header.
import { uploadToS3 } from '@/utils/fileUpload';
// Browser-side normalization of whatever the practice picked (webp/svg/jpg/gif) into the
// one format every email client renders: PNG. The extension list lives there too, so the
// <input accept> and the rejection message can never drift apart.
import {
  ACCEPTED_LOGO_EXTENSIONS,
  describeLogoFileRejection,
  normalizeLogoFile,
  browserLogoDeps,
  looksLikeEmailUnsafeImageUrl,
  isTeamioUploadUrl,
} from '@/utils/signatureLogoFile.js';

// Set base URL for API calls
axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

/** The one settings row the signature lives in. Full-blob replace — never JSON_SET. */
export const EMAIL_SIGNATURE_SETTING = 'EMAIL_SIGNATURE';

/**
 * Shown when a practice switches the signature ON but has filled in nothing that renders.
 * The save is REFUSED in that state rather than stored-and-toasted-success: an enabled
 * signature with empty html appends nothing to any email while the screen claims it is
 * configured — the Kanban tag-alert failure exactly (CLAUDE.md, "Settings that gate a send").
 */
export const SIGNATURE_EMPTY_MESSAGE = 'Your signature is switched on but empty, so nothing '
  + 'would be added to your emails. Add at least a practice name, logo, or link — or switch '
  + 'the signature off.';

// ── Pure, side-effect-free helpers — mirrored in
//    cypress/node-tests/email-settings-state.test.js. KEEP IN SYNC with that mirror.

/**
 * Classify a site into exactly one email-setup state from its DERIVED capability
 * plus whatever sender/domain records the backend returns:
 *   active  — the site can already send (GENERAL_VERIFIED_EMAILS resolves to a usable
 *             address, i.e. isEmailEnabled === true). Read-only for practice staff.
 *   pending — not yet sendable, but a sender or domain record exists (verification or
 *             DNS still outstanding). Resume / wait view.
 *   none    — nothing started. Guided wizard.
 *
 * `emailEnabled` is the ground truth for ACTIVE (from isEmailEnabled). The backend
 * sender/domain records only disambiguate pending-vs-none.
 */
export function classifyEmailState({ emailEnabled, hasSender, hasDomain }) {
  if (emailEnabled) return 'active';
  if (hasSender || hasDomain) return 'pending';
  return 'none';
}

/**
 * One 60s poll tick fires only while waiting on a pending verification AND the tab is
 * visible — same visibility-aware, stop-when-done contract as the A2P wizard's
 * shouldPollTick (src/pages/MyAccount/A2P/A2PWizardStatus.vue).
 */
export function shouldPollEmailTick(state, hidden) {
  if (hidden) return false;
  return state === 'pending';
}

/**
 * Sender/domain deletes hit a SHARED SendGrid account, so only Teamio internal
 * (Super Admin) may see the remove controls — never dental-practice staff, in any state.
 */
export function canRemoveSenders(role) {
  return role === 'Super Admin';
}

/**
 * The domain part of an email address (lowercased), or '' when there isn't one.
 * Pure — mirrored in cypress/node-tests/email-settings-state.test.js.
 */
export function emailDomainPart(email) {
  if (!email || typeof email !== 'string') return '';
  const at = email.indexOf('@');
  if (at === -1) return '';
  return email.slice(at + 1).trim().toLowerCase();
}

/**
 * Is `email` a syntactically valid single address whose domain is EXACTLY `domain`
 * (case-insensitive)? The domain flow enables whatever address the frontend supplies,
 * so a sending address that doesn't belong to the domain being authenticated would
 * enable the wrong (or an unauthenticated) domain — reject it client-side.
 * Pure — mirrored in cypress/node-tests/email-settings-state.test.js.
 */
export function isEmailAtDomain(email, domain) {
  if (!email || !domain) return false;
  const e = String(email).trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return false;
  return emailDomainPart(e) === String(domain).trim().toLowerCase();
}

/**
 * Should an email-settings request be retried after a failure? Only a 401 (the caller's
 * token was missing/expired — e.g. Firebase session restore hadn't finished when the tab
 * mounted), and only if we haven't already retried once. Anything else, or a second 401,
 * is surfaced. Pure — mirrored in cypress/node-tests/email-settings-state.test.js.
 */
export function shouldRetryAuth(status, alreadyRetried) {
  if (alreadyRetried) return false;
  return status === 401;
}

export default {
  name: 'EmailSettingsTab',

  data() {
    return {
      loading: true,
      submitting: false,
      emailFromName: '',
      savingFromName: false,
      resending: false,
      checking: false,
      validating: false,

      // "Email these records to our admins" (POST /email-settings/email-dns-records)
      sendingDnsEmail: false,
      dnsExtraRecipients: '',

      // Selected verification method: 'single' | 'domain' | null
      selectedMethod: null,

      // Single-sender form data
      senderForm: {
        nickname: '',
        from_name: '',
        from_email: '',
        reply_to: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        country: 'USA'
      },

      // Domain authentication form data
      domainForm: {
        domain: '',
        subdomain: ''
      },

      // The address the site will send from (domain flow). Collected up front, must be
      // AT the entered domain, and passed as `email` on every validate/poll so the
      // backend lights up email capability once the DNS verifies. Kept across the
      // authenticate→pending transition (survives resetDomainForm).
      domainSendingEmail: '',

      // The exact single-sender payload we submitted, so "Check Status" can re-invoke the
      // backend's idempotent enable path without depending on SendGrid's response shape.
      pendingSenderPayload: null,

      // Validation state
      formSubmitted: false,
      domainFormSubmitted: false,

      // Backend sender/domain records
      verifiedSender: null,
      pendingSender: null,
      authenticatedDomain: null,
      allSenders: [],

      // Modal state
      showDeleteModal: false,
      showDeleteDomainModal: false,

      // ── Email signature ──────────────────────────────────────────────────
      // The editable config. `fields` mirrors the stored blob's `fields` exactly;
      // `links` is [{label, value}]. Rendered html/text are DERIVED at save time.
      signature: {
        enabled: false,
        fields: emptySignatureFields(),
        links: [],
      },
      // Hydration happens once, from either the saved blob or the sites row — never
      // both, and never twice, so a settings refresh after save cannot clobber edits.
      signatureHydrated: false,
      savingSignature: false,
      // The preview <img> failed to load ⇒ warn, and don't claim a logo will render.
      signatureLogoBroken: false,
      // Natural pixel size of the loaded preview image. Feeds the renderer's Outlook
      // width/height attributes — CSS max-width/max-height is not a cap in Outlook.
      signatureLogoDims: null,
      // An upload is in flight (convert + presign + PUT). Blocks Save: the URL isn't
      // known yet, so a save now would store the OLD logo and report success.
      uploadingSignatureLogo: false,
      // The office asked for the paste-a-link field. Upload is the primary path; the
      // link field is demoted behind this toggle (see signatureLogoPasteVisible).
      signatureLogoPasteOpen: false,
      // '.png,.jpg,...' straight from the util so the file dialog and the rejection
      // message can never disagree about what is accepted.
      acceptedLogoExtensions: ACCEPTED_LOGO_EXTENSIONS,

      // Poll timer (pending-state auto-refresh)
      pollTimer: null,

      // Set when the caller has no Firebase token even after waiting for session restore
      // (a genuinely signed-out-of-Firebase session) — we show an inline warn instead of
      // silently rendering a tab whose every API call 401s.
      authUnavailable: false,

      // Country options
      countryOptions: [
        { value: 'USA', text: 'United States' },
        { value: 'CAN', text: 'Canada' },
        { value: 'GBR', text: 'United Kingdom' },
        { value: 'AUS', text: 'Australia' },
        { value: 'DEU', text: 'Germany' },
        { value: 'FRA', text: 'France' },
        { value: 'IND', text: 'India' },
        { value: 'MEX', text: 'Mexico' }
      ]
    };
  },

  computed: {
    ...mapState('customer', ['sites', 'siteID', 'user', 'settings']),
    ...mapGetters('customer', ['getSite']),

    // Exposed to the template so the refusal copy lives in exactly one place.
    SIGNATURE_EMPTY_MESSAGE: () => SIGNATURE_EMPTY_MESSAGE,

    currentSiteId() {
      return this.siteID || (this.user && this.user.SiteID);
    },

    // Derived email capability — the ground truth for the ACTIVE state.
    emailEnabled() {
      return isEmailEnabled(this.settings);
    },

    // The active sender address, resolved from GENERAL_VERIFIED_EMAILS via the shared
    // five-shape parser (the same value every send path will use).
    activeFromEmail() {
      return pickVerifiedFromEmail(readSettingValue(this.settings, EMAIL_CAPABILITY_SETTING));
    },

    activeFromName() {
      return readSettingValue(this.settings, 'EMAIL_FROM_NAME');
    },

    // The practice's own name, used as the DEFAULT display name when EMAIL_FROM_NAME has
    // never been set (2026-09-17: the field sat empty and the placeholder said "Mint Dental",
    // so every new practice saw another practice's name as the example). A SAVED value always
    // wins — this only fills the blank, and the office still has to press Save.
    practiceDisplayName() {
      const site = this.getSite(this.currentSiteId);
      const fromSite = site && (site.Name || site.SiteName);
      return String(fromSite || readSettingValue(this.settings, 'GENERAL_NAME') || '').trim();
    },

    hasSender() {
      return !!(this.verifiedSender || this.pendingSender) || (this.allSenders && this.allSenders.length > 0);
    },

    hasDomain() {
      return !!this.authenticatedDomain;
    },

    // State machine: exactly one of active / pending / none.
    emailState() {
      return classifyEmailState({
        emailEnabled: this.emailEnabled,
        hasSender: this.hasSender,
        hasDomain: this.hasDomain
      });
    },

    isSuperAdmin() {
      return !!(this.user && this.user.Role === 'Super Admin');
    },

    // The owner's no-touch rule: a site that already has working email must not be able
    // to change anything. Teamio internal (Super Admin) is exempt — they manage on the
    // practice's behalf.
    isReadOnly() {
      return this.emailState === 'active' && !this.isSuperAdmin;
    },

    canSeeRemove() {
      return canRemoveSenders(this.user && this.user.Role);
    },

    dnsRecords() {
      if (!this.authenticatedDomain || !this.authenticatedDomain.dns) return [];
      return this.formatDnsRecords(this.authenticatedDomain.dns);
    },

    // The domain we validate the sending address against — the pending domain once it
    // exists, otherwise whatever is being typed into the form.
    effectiveDomainName() {
      return (this.authenticatedDomain && this.authenticatedDomain.domain) ||
             (this.domainForm && this.domainForm.domain) || '';
    },

    domainEmailValid() {
      return isEmailAtDomain(this.domainSendingEmail, this.effectiveDomainName);
    },

    domainEmailMismatch() {
      const e = (this.domainSendingEmail || '').trim();
      return e.length > 0 && !this.domainEmailValid;
    },

    isFormValid() {
      return this.senderForm.from_name && this.senderForm.from_name.trim() &&
             this.isValidEmail(this.senderForm.from_email) &&
             this.senderForm.address && this.senderForm.address.trim() &&
             this.senderForm.city && this.senderForm.city.trim() &&
             this.senderForm.country && this.senderForm.country.trim();
    },

    isDomainFormValid() {
      // Require the sending address up front, and reject one that isn't at the domain.
      return this.isValidDomain(this.domainForm.domain) &&
             isEmailAtDomain(this.domainSendingEmail, this.domainForm.domain);
    },

    // ── Email signature ──────────────────────────────────────────────────────

    /** The blob currently stored for this site, or null (absent / corrupt). */
    storedSignature() {
      return parseSignatureSetting(readSettingValue(this.settings, EMAIL_SIGNATURE_SETTING));
    },

    /**
     * Exactly the shape the renderer and the save gate consume — trimmed, blank link
     * rows dropped. Built ONCE here so the preview, the gate and the saved blob can
     * never be computed from three slightly different objects.
     */
    signatureConfig() {
      const f = this.signature.fields || {};
      const t = (v) => String(v == null ? '' : v).trim();
      return {
        fields: {
          showLogo: f.showLogo !== false,
          logoUrl: t(f.logoUrl),
          practiceName: t(f.practiceName),
          phone: t(f.phone),
          address1: t(f.address1),
          address2: t(f.address2),
          city: t(f.city),
          state: t(f.state),
          zip: t(f.zip),
          website: t(f.website),
          tagline: t(f.tagline),
        },
        links: (this.signature.links || [])
          .map((l) => ({ label: t(l && l.label), value: t(l && l.value) }))
          .filter((l) => l.value !== ''),
      };
    },

    /** Only a safe http(s) URL is ever put in the preview <img src>. */
    signatureLogoUrl() {
      return sanitizeSignatureUrl(this.signature.fields && this.signature.fields.logoUrl);
    },

    signatureLogoBoxLabel() {
      return `${LOGO_BOX.maxW}x${LOGO_BOX.maxH}px`;
    },

    /**
     * Is the paste-a-link field on screen?
     *
     * Upload is the primary path, so the field is hidden by default — but a site that
     * already has a pasted (non-Teamio) URL must keep SEEING it, or the value it is
     * about to email out becomes invisible and unfixable. An uploaded Teamio URL needs
     * no such escape: "Upload logo" replaces it and "Remove logo" clears it.
     */
    signatureLogoPasteVisible() {
      const url = this.signature.fields && this.signature.fields.logoUrl;
      return this.signatureLogoPasteOpen || (!!url && !isTeamioUploadUrl(url));
    },

    /** A pasted .webp/.svg link — renders here, blank in plenty of email clients. */
    signatureLogoPasteWarning() {
      return looksLikeEmailUnsafeImageUrl(this.signature.fields && this.signature.fields.logoUrl);
    },

    /** Typed something in Website that is neither a URL nor a bare domain ⇒ it is dropped. */
    signatureWebsiteInvalid() {
      const raw = this.signatureConfig.fields.website;
      return raw !== '' && normalizeWebsiteUrl(raw) === '';
    },

    /** What the office will actually get. Same function every consumer's html came from. */
    renderedSignature() {
      return renderEmailSignature(this.signatureConfig, { logoDims: this.signatureLogoDims });
    },

    /** Enabled but nothing renders — the save is refused in this state. */
    signatureBlocked() {
      return this.signature.enabled === true && !isRenderableSignatureConfig(this.signatureConfig);
    }
  },

  async mounted() {
    await this.loadAllData();
    // Kick off polling if we mounted straight into the pending state.
    if (this.emailState === 'pending') this.startPolling();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  watch: {
    // Reload when siteID becomes available (customer store hydrates after mount).
    siteID: {
      immediate: false,
      handler(newVal) {
        if (newVal) this.loadAllData();
        this.hydrateSignature();
      }
    },

    // The signature form fills itself from whichever source arrives first — the saved
    // blob (settings) or, when there is none, the sites row (prefill). Both stores
    // hydrate asynchronously after mount, so watch both; hydrateSignature() is a no-op
    // once it has succeeded, which is what stops the post-save getSettings refresh from
    // throwing away whatever the user has typed since.
    settings: {
      immediate: true,
      handler() { this.hydrateSignature(); }
    },

    sites: {
      immediate: true,
      handler() { this.hydrateSignature(); }
    },

    // A new logo URL invalidates both the "broken" verdict and the cached natural size.
    'signature.fields.logoUrl': function onSignatureLogoUrlChange() {
      this.signatureLogoBroken = false;
      this.signatureLogoDims = null;
    },

    // Start/stop the auto-refresh poll as the site enters/leaves the pending state,
    // and announce a successful verification once.
    emailState(newState, oldState) {
      if (newState === 'pending') {
        this.startPolling();
      } else {
        this.stopPolling();
      }
      if (newState === 'active' && oldState === 'pending') {
        this.$toast.success('Your email sender is verified — you\'re all set to send email.');
      }
    }
  },

  methods: {
    ...mapActions('customer', ['upsertSettings', 'getSettings']),

    // ── Auth ─────────────────────────────────────────────────────────────────
    // Same credential pattern as the other authed main-service callers
    // (WhatsAppOnboarding.vue, store/myaccount.js): a Firebase ID token that the
    // hardened /email-settings/* endpoints verify with verifyIdToken, sent in the
    // Authorization: Bearer header on every request (GET, POST, DELETE).
    async authToken() {
      try {
        const auth = this.$store.state.getAuth();
        if (auth && auth.currentUser) return await auth.currentUser.getIdToken();
      } catch (e) {
        console.log('EmailSettings: could not get ID token', e);
      }
      return null;
    },

    async authConfig(extra) {
      const base = extra || {};
      const idToken = await this.authToken();
      const headers = Object.assign({}, base.headers || {});
      if (idToken) headers.Authorization = `Bearer ${idToken}`;
      return Object.assign({ crossdomain: true }, base, { headers });
    },

    // Settings.vue's b-tabs mounts this component EAGERLY at page load, so mounted()
    // can run before Firebase's async session restore finishes — at which point
    // getAuth().currentUser is null and every call would 401 with no retry. Wait for
    // the first auth state to settle (resolve immediately if a user is already present),
    // with a hard timeout so a genuinely signed-out session never hangs the tab.
    async waitForAuthReady(timeoutMs) {
      const limit = typeof timeoutMs === 'number' ? timeoutMs : 8000;
      const auth = this.$store.state.getAuth ? this.$store.state.getAuth() : null;
      if (auth && auth.currentUser) return true;
      if (!auth) return false;

      let onAuthStateChanged;
      try {
        ({ onAuthStateChanged } = await import('firebase/auth'));
      } catch (e) {
        console.log('EmailSettings: could not load firebase/auth', e);
        return false;
      }

      return new Promise((resolve) => {
        let settled = false;
        let unsub = null;
        const cleanup = () => { if (unsub) { try { unsub(); } catch (e) { /* ignore */ } unsub = null; } };
        const finish = (val) => { if (settled) return; settled = true; clearTimeout(timer); cleanup(); resolve(val); };
        const timer = setTimeout(() => finish(false), limit);
        // onAuthStateChanged fires once immediately with the current user, then on change.
        // Resolve only on a non-null user (restore completed); otherwise wait / time out.
        unsub = onAuthStateChanged(auth, (user) => { if (user) finish(true); });
        if (settled) cleanup(); // handle a synchronous immediate fire (unsub was still null)
      });
    },

    // Belt-and-braces: if a request 401s (auth wasn't ready yet), give session restore a
    // moment and retry ONCE with a freshly-built token. `doRequest` must rebuild its
    // authConfig()/authToken() each call so the retry carries the new credential.
    async withAuthRetry(doRequest) {
      let attempt = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          return await doRequest();
        } catch (err) {
          const status = err && err.response && err.response.status;
          if (!shouldRetryAuth(status, attempt > 0)) throw err;
          attempt += 1;
          await this.waitForAuthReady(3000);
        }
      }
    },

    // ── Data loading ─────────────────────────────────────────────────────────
    async saveEmailFromName() {
      this.savingFromName = true;
      try {
        await this.upsertSettings({
          settings: { EMAIL_FROM_NAME: this.emailFromName.trim() },
          siteID: this.currentSiteId,
        });
        await this.getSettings({ siteID: this.currentSiteId });
        this.$toast.success('Email display name saved successfully!');
      } catch (err) {
        console.error('Error saving email from name:', err);
        this.$toast.error('Failed to save. Please try again.');
      } finally {
        this.savingFromName = false;
      }
    },

    // ── Email signature ──────────────────────────────────────────────────────

    /**
     * Fill the form ONCE, from the saved blob when there is one and from the site's own
     * record when there isn't.
     *
     * A saved blob is never merged with prefill: an office that deliberately cleared a
     * field must not have the sites row silently put it back on the next page load.
     * Prefill is strictly the empty-state convenience.
     */
    hydrateSignature() {
      if (this.signatureHydrated) return;

      const stored = this.storedSignature;
      if (stored) {
        this.signature = {
          enabled: stored.enabled === true,
          fields: Object.assign(emptySignatureFields(), stored.fields || {}),
          links: (Array.isArray(stored.links) ? stored.links : [])
            .filter((l) => l && typeof l === 'object')
            .map((l) => ({ label: String(l.label == null ? '' : l.label), value: String(l.value == null ? '' : l.value) })),
        };
        this.signatureHydrated = true;
        return;
      }

      // No saved config. Prefill from teamio.sites (exact PascalCase columns). If the
      // sites list hasn't arrived yet, stay UNhydrated so a later tick can still do it.
      const site = this.currentSiteId && this.getSite ? this.getSite(this.currentSiteId) : null;
      if (!site) return;

      const fields = emptySignatureFields();
      fields.logoUrl = site.LogoUrl || '';
      fields.practiceName = site.Name || '';
      fields.phone = site.OfficePhone || '';
      fields.address1 = site.Address1 || '';
      fields.address2 = site.Address2 || '';
      fields.city = site.City || '';
      fields.state = site.State || '';
      fields.zip = site.Zip || '';
      this.signature = { enabled: false, fields, links: [] };
      this.signatureHydrated = true;
    },

    /** @load hands us the natural size (Outlook needs real width/height); @error passes null. */
    onSignatureLogoLoad(ev) {
      if (!ev || !ev.target || !ev.target.naturalWidth) {
        this.signatureLogoBroken = true;
        this.signatureLogoDims = null;
        return;
      }
      this.signatureLogoBroken = false;
      this.signatureLogoDims = {
        width: ev.target.naturalWidth,
        height: ev.target.naturalHeight,
      };
    },

    /**
     * The office picked a file. Convert it to PNG in the browser, upload it to our own
     * bucket, and point the signature at the result.
     *
     * teamioType: 'FORM' puts the object under forms/<siteID>/ — public by bucket policy
     * with NO lifecycle expiry. temp/ (what WIKI-type uploads get) expires in 30 days and
     * chat/ in 90; a signature logo has to still be there when a patient opens a
     * six-month-old email. compress: false because normalizeLogoFile already produced the
     * final PNG at the right size — re-compressing would re-encode it for nothing.
     */
    async onSignatureLogoFilePicked(ev) {
      const input = ev && ev.target;
      const file = input && input.files && input.files[0];
      if (!file) return;

      // Cheap, local checks first (HEIC, PDF, >10MB, unknown) so an unusable file never
      // costs a presign round-trip and the office gets a plain-English reason.
      const rejection = describeLogoFileRejection(file);
      if (rejection) {
        this.$toast.error(rejection);
        // Clearing the input is what lets the office re-pick the SAME file after fixing
        // it — <input type="file"> fires no `change` when the value is unchanged.
        input.value = '';
        return;
      }

      this.uploadingSignatureLogo = true;
      try {
        const normalized = await normalizeLogoFile(file, browserLogoDeps());
        const uploaded = await uploadToS3({
          file: normalized.file,
          siteID: this.currentSiteId,
          teamioType: 'FORM',
          compress: false,
        }).catch((err) => {
          // normalizeLogoFile throws plain-English messages; the uploader throws transport
          // errors ("Request failed with status code 500"). Log the real one, show a
          // sentence the front desk can act on.
          console.error('Signature logo S3 upload failed:', err);
          throw new Error('We couldn\'t upload that logo. Check your connection and try again.');
        });
        this.signature.fields.logoUrl = uploaded.url;
        this.signature.fields.showLogo = true;
        // The 'signature.fields.logoUrl' watcher nulls signatureLogoDims, and it runs on
        // the NEXT tick. Setting the dims after that tick is what makes them stick; the
        // preview's @load then reconfirms them from the real image. Without the wait, a
        // Save fired before the S3 <img> finishes loading renders CSS-only markup, which
        // Outlook desktop ignores — the logo lands at its full 400px.
        await this.$nextTick();
        this.signatureLogoBroken = false;
        this.signatureLogoDims = { width: normalized.width, height: normalized.height };
        this.$toast.success('Logo uploaded — click Save to use it in your signature.');
      } catch (err) {
        console.error('Signature logo upload failed:', err);
        this.$toast.error((err && err.message) || 'We couldn\'t upload that logo. Please try again.');
      } finally {
        this.uploadingSignatureLogo = false;
        // Same reason as above: re-picking the identical file must fire `change`.
        if (input) input.value = '';
      }
    },

    /**
     * Drop the logo from the signature.
     *
     * ⛔ Deliberately does NOT delete the S3 object. Emails already in patients' inboxes
     * fetch the logo when they are OPENED — deleting it breaks every message the practice
     * has ever sent. An orphaned PNG in forms/<siteID>/ costs fractions of a cent.
     *
     * showLogo is left alone: the renderer already drops the <img> when the URL is empty,
     * so flipping the checkbox here would just lose the office's preference for the next
     * logo they upload.
     */
    removeSignatureLogo() {
      this.signature.fields.logoUrl = '';
    },

    addSignatureLink(label) {
      this.signature.links.push({ label: label || '', value: '' });
    },

    removeSignatureLink(index) {
      this.signature.links.splice(index, 1);
    },

    /**
     * Save the signature as ONE full-blob replace of EMAIL_SIGNATURE.
     *
     * ⛔ REFUSES an enabled config that renders nothing. Storing it would report success
     * for a signature that can never appear in an email — the store-and-toast-success
     * failure CLAUDE.md forbids for any setting that gates a send. The gate is the same
     * predicate the renderer is built from, so anything this accepts provably renders.
     */
    async saveSignature() {
      // Saving mid-upload would store the PREVIOUS logoUrl and toast success.
      if (this.uploadingSignatureLogo) {
        this.$toast.error('Still uploading your logo — try again in a second.');
        return;
      }
      // Saving before the preview MEASURED the image stores CSS-only <img> markup;
      // Outlook desktop ignores CSS sizing and renders the logo at full size.
      if (this.signature.fields.showLogo !== false && this.signatureLogoUrl
        && !this.signatureLogoDims && !this.signatureLogoBroken) {
        this.$toast.error('Still checking your logo — try again in a second.');
        return;
      }

      const config = this.signatureConfig;

      if (this.signature.enabled && !isRenderableSignatureConfig(config)) {
        this.$toast.error(SIGNATURE_EMPTY_MESSAGE);
        return;
      }

      this.savingSignature = true;
      try {
        const rendered = renderEmailSignature(config, { logoDims: this.signatureLogoDims });
        const blob = {
          version: SIGNATURE_VERSION,
          enabled: this.signature.enabled === true,
          fields: config.fields,
          links: config.links,
          html: rendered.html,
          text: rendered.text,
          updatedAt: new Date().toISOString(),
        };
        // ASCII-safe JSON: PropertyValue is a latin1 column and truncates/mangles
        // multi-byte characters SILENTLY (Smile Bar, 2026-06-08).
        await this.upsertSettings({
          settings: { [EMAIL_SIGNATURE_SETTING]: asciiSafeStringifySignature(blob) },
          siteID: this.currentSiteId,
        });
        await this.getSettings({ siteID: this.currentSiteId });
        this.$toast.success(
          blob.enabled
            ? 'Email signature saved — on for Treatment Plan emails and wherever you\'ve placed {{email_signature_block}}.'
            : 'Email signature saved. It stays off until you switch it on.'
        );
      } catch (err) {
        console.error('Error saving email signature:', err);
        this.$toast.error('Failed to save your signature. Please try again.');
      } finally {
        this.savingSignature = false;
      }
    },

    async loadAllData() {
      if (!this.currentSiteId) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.authUnavailable = false;
      try {
        // Wait for Firebase session restore before any authed call (fixes the eager-mount
        // 401). If there's still no token afterwards, the session is genuinely signed out.
        await this.waitForAuthReady();
        const token = await this.authToken();
        if (!token) {
          this.authUnavailable = true;
          return;
        }

        // A saved EMAIL_FROM_NAME always wins; only a blank one defaults to the practice name.
        this.emailFromName = this.activeFromName || this.practiceDisplayName;
        await this.loadSenders();
        await this.loadDomains();
      } catch (err) {
        console.error('Error loading data:', err);
        const status = err && err.response && err.response.status;
        if (status === 401) {
          // Token was rejected even after the ready-wait + single retry — surface the
          // fresh-login prompt rather than a generic failure toast.
          this.authUnavailable = true;
        } else {
          this.$toast.error('Failed to load email settings. Please try again.');
        }
      } finally {
        this.loading = false;
      }
    },

    async loadSenders() {
      try {
        const response = await this.withAuthRetry(async () => axios.get('/email-settings/verified-senders', await this.authConfig({
          params: { siteID: this.currentSiteId }
        })));

        if (response.data && response.data.senders) {
          this.allSenders = response.data.senders;
          this.verifiedSender = this.allSenders.find(s => s.verified) || null;
          this.pendingSender = this.verifiedSender ? null : (this.allSenders.find(s => !s.verified) || null);
        }
      } catch (err) {
        console.error('Error loading senders:', err);
        throw err;
      }
    },

    async loadDomains() {
      try {
        const response = await this.withAuthRetry(async () => axios.get('/email-settings/authenticated-domains', await this.authConfig({
          params: { siteID: this.currentSiteId }
        })));

        if (response.data && response.data.domains && response.data.domains.length > 0) {
          this.authenticatedDomain = response.data.domains[0];
        } else {
          this.authenticatedDomain = null;
        }
      } catch (err) {
        // Domain endpoint might not exist yet, that's ok.
        console.log('Domain auth endpoint not available yet:', err.message);
      }
    },

    // Refresh the pieces the state machine reads: settings (for GENERAL_VERIFIED_EMAILS
    // written on verification) + the sender/domain records.
    async refreshAll() {
      await this.getSettings({ siteID: this.currentSiteId });
      await this.loadSenders();
      await this.loadDomains();
    },

    formatDnsRecords(dns) {
      if (!dns) return [];
      const records = [];
      Object.keys(dns).forEach(key => {
        const record = dns[key];
        if (record && record.host) {
          records.push({
            key,
            type: record.type || 'CNAME',
            host: record.host,
            data: record.data,
            valid: record.valid,
            // SendGrid returns the failure reason under a couple of shapes — show it verbatim.
            reason: record.reason || (record.validation_results && record.validation_results.reason) || ''
          });
        }
      });
      return records;
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$toast.success('Copied to clipboard!');
      }).catch(() => {
        this.$toast.error('Failed to copy.');
      });
    },

    // ── Polling ──────────────────────────────────────────────────────────────
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => this.pollTick(), 60000);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async pollTick() {
      const hidden = typeof document !== 'undefined' && document.hidden;
      if (!shouldPollEmailTick(this.emailState, hidden)) {
        // Not pending anymore (or tab hidden) — stop entirely if we've left pending.
        if (this.emailState !== 'pending') this.stopPolling();
        return;
      }
      try {
        if (this.authenticatedDomain) {
          // Domain flow: re-validate DNS each tick; the backend auto-enables once the
          // domain is valid AND the matching sending address is supplied (safe to repeat).
          await this.validateDomain({ silent: true });
        } else {
          // Single-sender flow: refresh settings/records only. The enable is user-initiated
          // via "Check Status" — we never auto-POST create-verified-sender, to avoid any
          // risk of resending the confirmation email.
          await this.refreshAll();
        }
      } catch (e) {
        console.error('EmailSettings poll refresh failed:', e);
      }
    },

    // ── Validation ───────────────────────────────────────────────────────────
    getFieldState(field) {
      if (!this.formSubmitted) return null;
      switch (field) {
        case 'from_name': return !!(this.senderForm.from_name && this.senderForm.from_name.trim().length > 0);
        case 'from_email': return this.isValidEmail(this.senderForm.from_email);
        case 'address': return !!(this.senderForm.address && this.senderForm.address.trim().length > 0);
        case 'city': return !!(this.senderForm.city && this.senderForm.city.trim().length > 0);
        case 'country': return !!(this.senderForm.country && this.senderForm.country.trim().length > 0);
        default: return null;
      }
    },

    getDomainFieldState(field) {
      if (!this.domainFormSubmitted) return null;
      if (field === 'domain') return this.isValidDomain(this.domainForm.domain);
      return null;
    },

    isValidEmail(email) {
      if (!email) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email.trim());
    },

    isValidDomain(domain) {
      if (!domain) return false;
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}$/;
      return domainRegex.test(domain.trim());
    },

    // ── Wizard submit ────────────────────────────────────────────────────────
    async submitVerification() {
      this.formSubmitted = true;
      if (!this.isFormValid) {
        this.$toast.error('Please fill in all required fields.');
        return;
      }
      this.submitting = true;
      try {
        const payload = {
          siteID: this.currentSiteId,
          nickname: this.senderForm.nickname || this.senderForm.from_name,
          from_name: this.senderForm.from_name,
          from_email: this.senderForm.from_email,
          reply_to: this.senderForm.reply_to || this.senderForm.from_email,
          address: this.senderForm.address,
          city: this.senderForm.city,
          state: this.senderForm.state || '',
          zip: this.senderForm.zip || '',
          country: this.senderForm.country
        };

        const response = await this.withAuthRetry(async () => axios.post('/email-settings/create-verified-sender', payload, await this.authConfig()));
        const data = response.data || {};

        if (data.success) {
          // Keep the payload so "Check Status" can re-invoke the idempotent enable path.
          this.pendingSenderPayload = payload;
          this.pendingSender = data.sender;
          this.selectedMethod = null;
          this.resetForm();
          if (data.needsVerification) {
            this.$toast.success('Verification email sent! Please check your inbox at ' + payload.from_email);
          } else {
            // Sender already verified — capability just lit up; refresh → ACTIVE.
            await this.getSettings({ siteID: this.currentSiteId });
          }
        } else {
          throw new Error(data.message || 'Failed to create sender');
        }
      } catch (err) {
        console.error('Error submitting verification:', err);
        const errorMessage = (err.response && err.response.data && err.response.data.message) || err.message || 'Failed to submit verification request.';
        this.$toast.error(errorMessage);
      } finally {
        this.submitting = false;
      }
    },

    async submitDomainAuth() {
      this.domainFormSubmitted = true;
      if (!this.isDomainFormValid) {
        this.$toast.error('Please enter a valid domain.');
        return;
      }
      this.submitting = true;
      try {
        const payload = {
          siteID: this.currentSiteId,
          domain: this.domainForm.domain.trim().toLowerCase(),
          subdomain: (this.domainForm.subdomain && this.domainForm.subdomain.trim()) || undefined
        };

        const response = await this.withAuthRetry(async () => axios.post('/email-settings/authenticate-domain', payload, await this.authConfig()));

        if (response.data && response.data.success) {
          this.$toast.success('Domain authentication started! Add the DNS records shown below.');
          this.authenticatedDomain = response.data.domain;
          this.selectedMethod = null;
          this.resetDomainForm(); // keeps domainSendingEmail for the validate/enable step
        } else {
          throw new Error((response.data && response.data.message) || 'Failed to authenticate domain');
        }
      } catch (err) {
        console.error('Error authenticating domain:', err);
        const errorMessage = (err.response && err.response.data && err.response.data.message) || err.message || 'Failed to authenticate domain.';
        this.$toast.error(errorMessage);
      } finally {
        this.submitting = false;
      }
    },

    // Button wrapper — Vue passes the click event to @click handlers, so keep validateDomain's
    // options object clean by routing the button through here.
    checkDomainAgain() {
      this.validateDomain({ silent: false });
    },

    async validateDomain(opts) {
      const silent = !!(opts && opts.silent);
      if (!this.authenticatedDomain) return;
      if (!silent) this.validating = true;
      try {
        const payload = {
          siteID: this.currentSiteId,
          domainId: this.authenticatedDomain.id
        };
        // Backend enables (writes GENERAL_VERIFIED_EMAILS) ONLY when the domain is valid
        // AND a sending address at this domain is supplied. Send it whenever we have a
        // valid one; omit it otherwise (then this call is a pure DNS-status poll).
        if (this.domainEmailValid) payload.email = this.domainSendingEmail.trim();

        const response = await this.withAuthRetry(async () => axios.post('/email-settings/validate-domain', payload, await this.authConfig()));
        const data = response.data || {};

        if (data.domain) this.authenticatedDomain = data.domain;

        if (data.enabled === true) {
          // Capability just lit up — refresh settings so emailState flips to ACTIVE
          // (the emailState watcher fires the success toast).
          await this.getSettings({ siteID: this.currentSiteId });
          return;
        }

        if (!silent) {
          if (data.valid) {
            this.$toast.info('Your domain is verified. Enter a sending address at this domain to finish enabling email.');
          } else {
            this.$toast.warning('DNS records not yet verified. Ensure all records are added correctly and allow up to 48 hours for propagation.');
          }
        }
      } catch (err) {
        console.error('Error validating domain:', err);
        if (!silent) {
          const msg = (err.response && err.response.data && err.response.data.message) || 'Failed to validate domain. Please try again.';
          this.$toast.error(msg);
        }
      } finally {
        if (!silent) this.validating = false;
      }
    },

    // Email the pending domain's DNS records to the practice's Admins. The server picks the
    // recipients and re-reads the records from SendGrid; we only name the site + domain.
    async emailDnsRecordsToAdmins() {
      if (!this.authenticatedDomain || this.sendingDnsEmail) return;
      const payload = {
        siteID: this.currentSiteId,
        domainId: this.authenticatedDomain.id
      };
      if (this.isSuperAdmin) {
        const extra = this.dnsExtraRecipients.split(',').map(e => e.trim()).filter(Boolean);
        if (extra.length) payload.extraRecipients = extra;
      }

      this.sendingDnsEmail = true;
      try {
        const response = await this.withAuthRetry(async () => axios.post('/email-settings/email-dns-records', payload, await this.authConfig()));
        const sentTo = (response.data && response.data.sentTo) || [];
        this.$toast.success('DNS records emailed to ' + sentTo.join(', '));
        this.dnsExtraRecipients = '';
      } catch (err) {
        console.error('Error emailing DNS records:', err);
        const msg = (err.response && err.response.data && err.response.data.message) || 'We could not email the DNS records. Please try again.';
        this.$toast.error(msg);
      } finally {
        this.sendingDnsEmail = false;
      }
    },

    async resendVerificationForPending() {
      if (!this.pendingSender) return;
      this.resending = true;
      try {
        // siteID in body (backend requirement); token in the Authorization header.
        await this.withAuthRetry(async () => axios.post('/email-settings/resend-verification', {
          siteID: this.currentSiteId,
          senderId: this.pendingSender.id
        }, await this.authConfig()));
        this.$toast.success('Verification email resent to ' + this.pendingSender.from_email);
      } catch (err) {
        console.error('Error resending verification:', err);
        const msg = (err.response && err.response.data && err.response.data.message) || 'Failed to resend verification email.';
        this.$toast.error(msg);
      } finally {
        this.resending = false;
      }
    },

    // Reconstruct the create-verified-sender payload from the pending SendGrid sender,
    // as a fallback if the originally-submitted payload isn't available.
    senderPayloadFromPending() {
      const s = this.pendingSender || {};
      return {
        siteID: this.currentSiteId,
        nickname: s.nickname || s.from_name,
        from_name: s.from_name,
        from_email: s.from_email,
        reply_to: s.reply_to || s.from_email,
        address: s.address,
        city: s.city,
        state: s.state || '',
        zip: s.zip || '',
        country: s.country
      };
    },

    async checkVerificationStatus() {
      this.checking = true;
      try {
        if (this.pendingSender) {
          // Re-invoke create-verified-sender: the backend's idempotent enable path writes
          // GENERAL_VERIFIED_EMAILS once SendGrid reports the sender verified; otherwise it
          // returns needsVerification with NO side effect (no duplicate confirmation email).
          const payload = this.pendingSenderPayload || this.senderPayloadFromPending();
          const response = await this.withAuthRetry(async () => axios.post('/email-settings/create-verified-sender', payload, await this.authConfig()));
          const data = response.data || {};
          if (data.success && !data.needsVerification) {
            await this.getSettings({ siteID: this.currentSiteId }); // → ACTIVE (watcher toasts)
          } else {
            this.$toast.warning('Email not yet verified. Please check your inbox and click the verification link.');
          }
        } else {
          await this.refreshAll();
          if (this.emailEnabled) {
            this.$toast.success('Email verified successfully! You\'re all set to send email.');
          } else {
            this.$toast.warning('Email not yet verified. Please check your inbox and click the verification link.');
          }
        }
      } catch (err) {
        console.error('Error checking status:', err);
        const msg = (err.response && err.response.data && err.response.data.message) || 'Failed to check verification status.';
        this.$toast.error(msg);
      } finally {
        this.checking = false;
      }
    },

    // "Start Over" for a pending single sender is a LOCAL reset — deleting the SendGrid
    // sender is a GLOBAL, Super-Admin-only server action, so staff cannot (and need not)
    // call it. The orphaned unverified sender is harmless; re-entering the same address
    // hits the backend's idempotent create path. Super Admin can delete it from the
    // active-state management card.
    cancelPending() {
      this.pendingSender = null;
      this.pendingSenderPayload = null;
      this.selectedMethod = null;
      this.$toast.success('Starting over. You can set up email again below.');
    },

    // ── Super-Admin-only deletes ──────────────────────────────────────────────
    confirmDelete() {
      this.showDeleteModal = true;
    },

    confirmDeleteDomain() {
      this.showDeleteDomainModal = true;
    },

    async deleteSender() {
      if (!this.verifiedSender) return;
      try {
        await this.withAuthRetry(async () => axios.delete(`/email-settings/verified-sender/${this.verifiedSender.id}`, await this.authConfig({
          params: { siteID: this.currentSiteId }
        })));
        this.$toast.success('Sender removed successfully.');
        this.verifiedSender = null;
        this.showDeleteModal = false;
        await this.refreshAll();
      } catch (err) {
        console.error('Error deleting sender:', err);
        // Server refusals (shared with another site / non-prod stage) carry the reason.
        const serverMessage = err.response && err.response.data && err.response.data.message;
        this.$toast.error(serverMessage || 'Failed to remove sender.');
      }
    },

    async deleteDomain() {
      if (!this.authenticatedDomain) return;
      try {
        await this.withAuthRetry(async () => axios.delete(`/email-settings/authenticated-domain/${this.authenticatedDomain.id}`, await this.authConfig({
          params: { siteID: this.currentSiteId }
        })));
        this.$toast.success('Domain authentication removed successfully.');
        this.authenticatedDomain = null;
        this.showDeleteDomainModal = false;
        await this.refreshAll();
      } catch (err) {
        console.error('Error deleting domain:', err);
        // Server refusals (shared with another site / non-prod stage) carry the reason.
        const serverMessage = err.response && err.response.data && err.response.data.message;
        this.$toast.error(serverMessage || 'Failed to remove domain authentication.');
      }
    },

    resetForm() {
      this.senderForm = {
        nickname: '', from_name: '', from_email: '', reply_to: '',
        address: '', city: '', state: '', zip: '', country: 'USA'
      };
      this.formSubmitted = false;
    },

    resetDomainForm() {
      this.domainForm = { domain: '', subdomain: '' };
      this.domainFormSubmitted = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.email-settings {
  .es-header {
    border-bottom: 1px solid #eee;
    padding-bottom: 1rem;
  }

  // ── Email signature card ───────────────────────────────────────────────────
  // Accent is teal #0f7259. NOT the `--teal-*` custom properties — those alias the
  // iris #5b5bd6 and would render this card purple (see CLAUDE.md design note).
  .signature-card {
    border: 1px solid #e0e0e0;
    border-radius: 8px;

    code {
      background: #f1f3f6;
      color: #0f7259;
      padding: 1px 5px;
      border-radius: 3px;
      font-size: 0.85em;
    }
  }

  .sig-toggle {
    ::v-deep .custom-control-label {
      cursor: pointer;
      padding-top: 1px;
    }
  }

  .sig-toggle-text {
    display: block;
    line-height: 1.4;
  }

  .sig-toggle-sub {
    display: block;
    font-size: 0.8rem;
    color: #6c757d;
  }

  .sig-logo-row {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .sig-logo-frame {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 140px;
    min-height: 92px;
    padding: 6px;
    border: 1px dashed #d5dbe0;
    border-radius: 6px;
    background: #fbfcfd;
  }

  // Height cap is mandatory, not cosmetic: one practice's LogoUrl is an 800x800
  // headshot. Enforced by cypress/node-tests/logo-size-cap-guardrail.test.js.
  .sig-logo-preview {
    max-width: 200px;
    max-height: 80px;
    width: auto;
    height: auto;
    object-fit: contain;
  }

  .sig-logo-empty {
    font-size: 0.8rem;
    color: #adb5bd;
  }

  .sig-logo-fields {
    flex: 1 1 320px;
    min-width: 260px;
  }

  .sig-logo-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .sig-show-logo {
    font-size: 0.875rem;
  }

  .sig-links {
    margin-top: 0.25rem;
  }

  .sig-link-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .sig-link-label {
    flex: 0 0 170px;
    max-width: 170px;
  }

  .sig-link-value {
    flex: 1 1 auto;
  }

  .sig-link-remove {
    flex: 0 0 auto;
    color: #adb5bd;

    &:hover {
      color: #dc3545;
    }
  }

  .sig-links-empty {
    font-size: 0.8rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
  }

  .sig-link-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .sig-preview {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;
  }

  .sig-preview--off {
    opacity: 0.5;
  }

  .sig-preview-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 6px 12px;
    background: #f4f8f7;
    border-bottom: 1px solid #e0e0e0;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #0f7259;
  }

  .sig-preview-off-pill {
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
    color: #6c757d;
  }

  .sig-preview-body {
    padding: 14px 16px;
  }

  .sig-preview-empty {
    padding: 14px 16px;
    font-size: 0.85rem;
    color: #adb5bd;
  }

  .sig-note {
    margin: 0.5rem 0 0;
    font-size: 0.8rem;
    color: #6c757d;
  }

  .sig-error {
    margin: 0.5rem 0 0;
    font-size: 0.825rem;
    color: #b45309;
  }

  // Card styles
  .method-choice-card,
  .active-summary-card,
  .done-card,
  .sa-management-card,
  .pending-sender-card,
  .pending-domain-card,
  .sender-form-card,
  .domain-form-card {
    border: 1px solid #e0e0e0;
    border-radius: 8px;

    ::v-deep .card-header {
      background: #f8f9fa;
      border-bottom: 1px solid #e0e0e0;
      padding: 1rem 1.25rem;
    }
  }

  .active-summary-card,
  .done-card {
    border-color: #28a745;

    ::v-deep .card-header {
      background: #f0fff4;
    }
  }

  .pending-sender-card,
  .pending-domain-card {
    border-color: #ffc107;

    ::v-deep .card-header {
      background: #fff9e6;
    }
  }

  .sa-management-card {
    border-color: #ffc107;
  }

  .card-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    i {
      font-size: 1.25rem;
    }

    .header-title {
      font-weight: 600;
      font-size: 1.1rem;
      color: #333;
    }
  }

  // Method choice options
  .method-options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .method-option {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #6c757d;
      background: #fafafa;
    }

    &.selected {
      border-color: #007bff;
      background: #f0f7ff;
    }

    .method-icon {
      flex-shrink: 0;
      width: 48px;
      height: 48px;
      background: #e9ecef;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 1.5rem;
        color: #495057;
      }
    }

    &.selected .method-icon {
      background: #007bff;

      i {
        color: white;
      }
    }

    .method-content {
      flex: 1;

      .method-title {
        margin-bottom: 0.5rem;
        font-weight: 600;
        color: #333;
      }

      .method-description {
        color: #666;
        font-size: 0.95rem;
      }

      .method-features {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          font-size: 0.9rem;
          color: #555;
          margin-bottom: 0.25rem;
        }
      }
    }

    .method-select {
      flex-shrink: 0;

      i {
        font-size: 1.5rem;
      }
    }
  }

  // How it works inline steps
  .how-it-works {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1rem 1.25rem;
  }

  .steps-inline {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .step-inline {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .step-num {
      width: 24px;
      height: 24px;
      background: #17a2b8;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.8rem;
      font-weight: 600;
    }
  }

  // Pending verification instructions
  .pending-instructions {
    .instruction-header {
      font-size: 1.05rem;
    }

    .instruction-steps {
      padding-left: 1.25rem;
      margin-bottom: 1rem;

      li {
        margin-bottom: 0.5rem;
        color: #555;
      }
    }

    .tip-alert {
      background: #f8f9fa;
      border: 1px solid #e0e0e0;
    }
  }

  .pending-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  // Sender details
  .sender-details {
    display: grid;
    gap: 0.75rem;
  }

  .detail-row {
    display: flex;
    align-items: center;
    gap: 1rem;

    .detail-label {
      font-weight: 500;
      color: #666;
      min-width: 110px;
    }

    .detail-value {
      color: #333;
    }
  }

  .support-note {
    font-size: 0.9rem;
    color: #555;
  }

  // "What you can do now" list
  .done-list {
    list-style: none;
    padding: 0;

    li {
      padding: 0.35rem 0;
      color: #444;
    }
  }

  // Super Admin extra recipients for "Email these records to our admins"
  .dns-extra-recipients {
    max-width: 400px;

    .dns-extra-input {
      width: 100%;
      padding: 0.375rem 0.5rem;
      font-size: 0.875rem;
      border: 1px solid #ced4da;
      border-radius: 4px;
    }
  }

  // DNS records (domain authentication)
  .dns-records {
    display: grid;
    gap: 1rem;
  }

  .dns-record {
    background: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 1rem;

    .dns-record-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.75rem;

      .dns-type {
        font-weight: 600;
        font-size: 0.8rem;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #6c757d;
      }
    }

    .dns-field {
      margin-bottom: 0.5rem;

      label {
        display: block;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #888;
        margin-bottom: 0.15rem;
      }
    }

    .dns-copy {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      background: white;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      padding: 0.35rem 0.5rem;

      code {
        font-size: 0.85rem;
        color: #333;
        word-break: break-all;
      }

      .copy-btn {
        color: #6c757d;
        flex-shrink: 0;

        &:hover {
          color: #007bff;
        }
      }
    }

    .dns-reason {
      margin-top: 0.5rem;
      font-size: 0.85rem;
      color: #b8860b;
    }
  }

  // Form sections
  .form-section {
    background: #f9fafb;
    border-radius: 8px;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
  }

  .form-section-title {
    font-weight: 600;
    color: #333;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;

    i {
      color: #6c757d;
    }
  }

  .form-label-custom {
    font-weight: 500;
    font-size: 0.9rem;
    color: #555;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 1rem;
    border-top: 1px solid #eee;
  }
}
</style>
